import React from 'react';
import { Box, LinearProgress, Typography, Chip } from '@mui/material';
import { FunnelSlide, FunnelGenerationStatus } from '../../types';

interface FunnelProgressIndicatorProps {
    slides: FunnelSlide[];
    totalSlides: number;
    currentStep: FunnelGenerationStatus;
}

const FunnelProgressIndicator: React.FC<FunnelProgressIndicatorProps> = ({
                                                                             slides,
                                                                             totalSlides,
                                                                             currentStep
                                                                         }) => {
    const completedCount = slides.filter(s => s.status === 'completed').length;
    const failedCount = slides.filter(s => s.status === 'failed').length;
    const pendingCount = totalSlides - completedCount - failedCount;

    const progressPercent = totalSlides > 0 ? (completedCount / totalSlides) * 100 : 0;

    const getStepLabel = (step: FunnelGenerationStatus): string => {
        switch (step) {
            case 'pending': return 'Initializing';
            case 'prompt_generating': return 'Generating prompts';
            case 'prompt_ready': return 'Prompts ready';
            case 'slides_generating': return 'Generating slides';
            case 'completed': return 'Completed';
            case 'failed': return 'Failed';
            default: return 'Unknown';
        }
    };

    const getStepColor = (step: FunnelGenerationStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        switch (step) {
            case 'completed': return 'success';
            case 'failed': return 'error';
            case 'slides_generating':
            case 'prompt_generating': return 'primary';
            case 'prompt_ready': return 'info';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ width: '100%', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">
                    Funnel Generation Progress
                </Typography>
                <Chip
                    label={getStepLabel(currentStep)}
                    color={getStepColor(currentStep)}
                    size="small"
                />
            </Box>

            <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                    height: 8,
                    borderRadius: 4,
                    mb: 1,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                    }
                }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {completedCount} of {totalSlides} slides completed
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {completedCount > 0 && (
                        <Chip
                            label={`✓ ${completedCount}`}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    )}
                    {pendingCount > 0 && (
                        <Chip
                            label={`⏳ ${pendingCount}`}
                            size="small"
                            color="default"
                            variant="outlined"
                        />
                    )}
                    {failedCount > 0 && (
                        <Chip
                            label={`✗ ${failedCount}`}
                            size="small"
                            color="error"
                            variant="outlined"
                        />
                    )}
                </Box>
            </Box>

            {progressPercent === 100 && failedCount === 0 && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 'medium' }}>
                    🎉 All slides generated successfully!
                </Typography>
            )}
        </Box>
    );
};

export default FunnelProgressIndicator;
